import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  fetchTrainerById,
  fetchTrainerActivity,
  fetchAllBatchesSimple,
} from "../pages/services/trainerService";
import { fetchAllSubjectsSimple } from "../pages/services/courseService";
import "../styles/StudentDetails.css";

// ─── Helpers ─────────────────────────────────────────────────────────────────
const ACTION_META = {
  LOGIN:          { label: "Logged In",       icon: "🔑", color: "#6366f1", bg: "#eef2ff" },
  TOPIC_UNLOCKED: { label: "Unlocked Topic",  icon: "🔓", color: "#0891b2", bg: "#e0f2fe" },
  NOTE_UPDATED:   { label: "Updated Notes",   icon: "📝", color: "#7c3aed", bg: "#f5f3ff" },
  BATCH_VIEWED:   { label: "Viewed Batch",    icon: "👁️", color: "#059669", bg: "#ecfdf5" },
  PAGE_VISIT:     { label: "Page Visit",      icon: "🖥️", color: "#b45309", bg: "#fffbeb" },
};

function fmtDuration(secs) {
  if (!secs || secs < 1) return null;
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function activityDescription(log) {
  switch (log.action) {
    case "TOPIC_UNLOCKED":
      return log.metadata?.topicTitle
        ? `"${log.metadata.topicTitle}" in ${log.metadata.batchTitle || "a batch"}`
        : log.metadata?.batchTitle || "";
    case "BATCH_VIEWED":
      return log.metadata?.batchTitle || "";
    case "NOTE_UPDATED":
      return log.metadata?.topicTitle || (log.metadata?.noteId ? `Note #${log.metadata.noteId.slice(-6)}` : "");
    case "PAGE_VISIT": {
      const dur   = fmtDuration(log.metadata?.durationSeconds);
      const title = log.metadata?.title ? `"${log.metadata.title}"` : "";
      const page  = log.metadata?.page  || "";
      return [page, title, dur ? `(${dur})` : ""].filter(Boolean).join(" · ");
    }
    default:
      return "";
  }
}

// ─── Component ────────────────────────────────────────────────────────────────
const TrainerDetails = () => {
  const navigate  = useNavigate();
  const { id }    = useParams();
  const location  = useLocation();

  const [trainer,      setTrainer]      = useState(location.state?.trainer || null);
  const [fetchingInfo, setFetchingInfo] = useState(!location.state?.trainer);
  const [allBatches,   setAllBatches]   = useState([]);
  const [allSubjects,  setAllSubjects]  = useState([]);
  const [logs,         setLogs]         = useState([]);
  const [logTotal,     setLogTotal]     = useState(0);
  const [logPage,      setLogPage]      = useState(1);
  const [fetchingLogs, setFetchingLogs] = useState(true);
  const [error,        setError]        = useState(null);
  const [successMsg,   setSuccessMsg]   = useState(location.state?.successMessage || null);

  useEffect(() => {
    if (successMsg) {
      // After save, re-fetch the trainer to reflect updated data
      fetchTrainerById(id).then(t => setTrainer(t)).catch(() => {});
      const t = setTimeout(() => setSuccessMsg(null), 4000);
      return () => clearTimeout(t);
    }
  }, [successMsg, id]);
  const LOG_LIMIT = 20;

  // Load trainer if not passed via nav state
  useEffect(() => {
    if (trainer) return;
    setFetchingInfo(true);
    fetchTrainerById(id)
      .then(t  => setTrainer(t))
      .catch(() => navigate("/trainers"))
      .finally(() => setFetchingInfo(false));
  }, [id, trainer, navigate]);

  // Load batch + subject reference data
  useEffect(() => {
    Promise.all([fetchAllBatchesSimple(), fetchAllSubjectsSimple()])
      .then(([batches, subjects]) => {
        setAllBatches((batches  || []).map(b => ({ ...b, _id: b._id.toString() })));
        setAllSubjects((subjects || []).map(s => ({ ...s, _id: s._id.toString() })));
      })
      .catch(() => {});
  }, []);

  // Load activity logs
  const loadLogs = useCallback(async (page) => {
    setFetchingLogs(true);
    try {
      const data = await fetchTrainerActivity(id, page, LOG_LIMIT);
      setLogs(prev => page === 1 ? data.logs : [...prev, ...data.logs]);
      setLogTotal(data.total);
      setLogPage(page);
    } catch {
      setError("Could not load activity logs.");
    } finally {
      setFetchingLogs(false);
    }
  }, [id]);

  useEffect(() => { loadLogs(1); }, [loadLogs]);

  if (fetchingInfo) {
    return (
      <div className="student-details-container">
        <div style={{ textAlign: "center", padding: "80px 20px", color: "#94a3b8" }}>
          <div className="lp-spinner" style={{ margin: "0 auto 16px" }} />
          <p>Loading trainer…</p>
        </div>
      </div>
    );
  }

  if (!trainer) return null;

  const isActive    = trainer.isActive    !== false;
  const isTracking  = trainer.trackingEnabled !== false;
  const initial     = (trainer.trainerName || "T")[0].toUpperCase();
  const hasMore     = logs.length < logTotal;

  // Cross-reference assigned IDs with fetched records
  const assignedBatchIds   = (trainer.assignedBatches  || []).map(id => id.toString());
  const assignedSubjectIds = (trainer.assignedSubjects || []).map(id => id.toString());
  const assignedBatchList   = allBatches.filter(b  => assignedBatchIds.includes(b._id));
  const assignedSubjectList = allSubjects.filter(s => assignedSubjectIds.includes(s._id));

  return (
    <div className="student-details-container">

      {successMsg && (
        <div className="success-toast" style={{ marginBottom: 16 }}>{successMsg}</div>
      )}
      {error && (
        <div style={{
          background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca",
          borderRadius: 10, padding: "10px 16px", marginBottom: 16, fontSize: 14,
        }}>{error}</div>
      )}

      {/* ── Header card ── */}
      <div className="student-header">
        <div className="student-header-title">
          <div className="student-avatar">{initial}</div>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
              {trainer.trainerName}
            </h2>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
              {trainer.contactNumber && (
                <span style={{ fontSize: 13, color: "#64748b" }}>📱 {trainer.contactNumber}</span>
              )}
              <span style={{
                fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                textTransform: "uppercase", letterSpacing: 0.4,
                background: isActive ? "#dcfce7" : "#fee2e2",
                color:      isActive ? "#15803d" : "#dc2626",
              }}>
                {isActive ? "Active" : "Disabled"}
              </span>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                textTransform: "uppercase", letterSpacing: 0.4,
                background: isTracking ? "#ede9fe" : "#f1f5f9",
                color:      isTracking ? "#6d28d9" : "#64748b",
              }}>
                {isTracking ? "Tracking ON" : "Tracking OFF"}
              </span>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="lp-btn-primary"
            style={{ padding: "8px 20px" }}
            onClick={() => navigate(`/trainers/${id}/update`, { state: { trainer } })}
          >
            ✏️ Edit Profile
          </button>
          <button
            className="lp-btn-secondary"
            style={{ padding: "8px 18px" }}
            onClick={() => navigate("/trainers")}
          >
            ← Back
          </button>
        </div>
      </div>

      {/* ── Batches + Subjects info cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>

        {/* Teaching Batches */}
        <div style={{
          background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.05)", overflow: "hidden",
        }}>
          <div style={{
            padding: "14px 20px", borderBottom: "1px solid #f1f5f9",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{ fontSize: 16 }}>🏫</span>
            <span style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Teaching Batches</span>
            <span style={{
              marginLeft: "auto", background: "#e0f2fe", color: "#0369a1",
              borderRadius: 20, padding: "2px 8px", fontSize: 12, fontWeight: 700,
            }}>
              {assignedBatchList.length}
            </span>
          </div>
          <div style={{ padding: "12px 16px", maxHeight: 200, overflowY: "auto" }}>
            {assignedBatchList.length === 0 ? (
              <p style={{ fontSize: 13, color: "#94a3b8", margin: 0, textAlign: "center", padding: "12px 0" }}>
                No batches assigned yet.
              </p>
            ) : (
              assignedBatchList.map(b => (
                <div key={b._id} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "8px 0", borderBottom: "1px solid #f8fafc",
                }}>
                  <span style={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>{b.batchTitle}</span>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
                    textTransform: "uppercase", letterSpacing: 0.3,
                    background: b.batchStatus === "Active" ? "#dcfce7" : "#f1f5f9",
                    color:      b.batchStatus === "Active" ? "#15803d" : "#64748b",
                  }}>
                    {b.batchStatus || "—"}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Learning Topics */}
        <div style={{
          background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.05)", overflow: "hidden",
        }}>
          <div style={{
            padding: "14px 20px", borderBottom: "1px solid #f1f5f9",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{ fontSize: 16 }}>📚</span>
            <span style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Learning Topics</span>
            <span style={{
              marginLeft: "auto", background: "#ede9fe", color: "#6d28d9",
              borderRadius: 20, padding: "2px 8px", fontSize: 12, fontWeight: 700,
            }}>
              {assignedSubjectList.length}
            </span>
          </div>
          <div style={{ padding: "12px 16px", maxHeight: 200, overflowY: "auto" }}>
            {assignedSubjectList.length === 0 ? (
              <p style={{ fontSize: 13, color: "#94a3b8", margin: 0, textAlign: "center", padding: "12px 0" }}>
                No learning topics assigned yet.
              </p>
            ) : (
              assignedSubjectList.map(s => (
                <div key={s._id} style={{
                  padding: "8px 0", borderBottom: "1px solid #f8fafc",
                  fontSize: 13, color: "#334155", fontWeight: 500,
                }}>
                  {s.title}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Activity Log ── */}
      <div style={{
        background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0",
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)", overflow: "hidden",
      }}>
        <div style={{
          padding: "18px 24px", borderBottom: "1px solid #f1f5f9",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <span style={{ fontSize: 18 }}>📋</span>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#0f172a" }}>Activity Log</h3>
            <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>
              {isTracking
                ? `${logTotal} event${logTotal !== 1 ? "s" : ""} recorded`
                : "Activity tracking is currently disabled for this trainer."}
            </p>
          </div>
        </div>

        {fetchingLogs && logs.length === 0 ? (
          <div style={{ padding: "40px 24px", textAlign: "center", color: "#94a3b8" }}>
            <div className="lp-spinner" style={{ margin: "0 auto 12px" }} />
            <p style={{ margin: 0, fontSize: 13 }}>Loading activity…</p>
          </div>
        ) : logs.length === 0 ? (
          <div style={{ padding: "48px 24px", textAlign: "center", color: "#94a3b8" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
            <p style={{ margin: 0, fontSize: 14 }}>No activity recorded yet.</p>
            <p style={{ margin: "4px 0 0", fontSize: 12 }}>
              {isTracking
                ? "Activity appears here once the trainer uses the app."
                : "Enable tracking to start recording activity."}
            </p>
          </div>
        ) : (
          <>
            <div style={{ padding: "8px 0" }}>
              {logs.map((log, idx) => {
                const meta = ACTION_META[log.action] || { label: log.action, icon: "•", color: "#64748b", bg: "#f8fafc" };
                const desc = activityDescription(log);
                return (
                  <div key={log._id || idx} style={{
                    display: "flex", alignItems: "flex-start", gap: 14,
                    padding: "14px 24px",
                    borderBottom: idx < logs.length - 1 ? "1px solid #f8fafc" : "none",
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                      background: meta.bg, display: "flex", alignItems: "center",
                      justifyContent: "center", fontSize: 16, marginTop: 1,
                    }}>
                      {meta.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{
                          fontSize: 13, fontWeight: 700, color: meta.color,
                          background: meta.bg, padding: "2px 8px", borderRadius: 20,
                        }}>
                          {meta.label}
                        </span>
                        {desc && (
                          <span style={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>
                            {desc}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                        {fmtDateTime(log.createdAt)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {hasMore && (
              <div style={{ padding: "14px 24px", borderTop: "1px solid #f1f5f9", textAlign: "center" }}>
                <button
                  onClick={() => loadLogs(logPage + 1)}
                  disabled={fetchingLogs}
                  style={{
                    background: "none", border: "1px solid #e2e8f0", borderRadius: 8,
                    padding: "8px 20px", fontSize: 13, fontWeight: 600, color: "#475569",
                    cursor: fetchingLogs ? "default" : "pointer",
                  }}
                >
                  {fetchingLogs ? "Loading…" : `Load more (${logTotal - logs.length} remaining)`}
                </button>
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
};

export default TrainerDetails;
